import { supabase } from '../utils/supabase';

const MAX_TEMPLATES_PER_USER = 5;
const MAX_NAME_LENGTH = 100;
const MAX_CONTENT_LENGTH = 5000;

class CoverLetterTemplateService {
  async getUserTemplates(userId) {
    if (!userId) {
      return this._createErrorResponse('User ID is required', [], 0);
    }

    try {
      const { data, error } = await supabase
        .from('cover_letter_templates')
        .select('id, name, content, usage_count, created_at')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('usage_count', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      return this._createSuccessResponse(data || [], data?.length || 0);
    } catch (error) {
      console.error('[CoverLetterTemplateService] getUserTemplates:', error);
      return this._createErrorResponse('Failed to load templates', [], 0);
    }
  }

  async getUserTemplateCount(userId) {
    if (!userId) {
      return { success: false, error: 'User ID is required', count: 0 };
    }

    try {
      const { data, error } = await supabase
        .rpc('get_user_cover_letter_template_count', { user_id_param: userId });

      if (error) throw error;
      return { success: true, count: data || 0 };
    } catch (error) {
      console.error('[CoverLetterTemplateService] getUserTemplateCount:', error);
      try {
        const { count, error: countError } = await supabase
          .from('cover_letter_templates')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_active', true);

        if (countError) throw countError;
        return { success: true, count: count || 0 };
      } catch (fallbackError) {
        console.error('[CoverLetterTemplateService] getUserTemplateCount fallback:', fallbackError);
        return { success: false, error: 'Failed to get template count', count: 0 };
      }    }
  }

  async createTemplate(userId, templateData) {
    if (!userId) {
      return this._createErrorResponse('User ID is required');
    }

    try {
      const validation = this._validateTemplateData(templateData);
      if (!validation.isValid) {
        return this._createErrorResponse(validation.error);
      }

      const countResult = await this.getUserTemplateCount(userId);
      if (countResult.count >= MAX_TEMPLATES_PER_USER) {
        return this._createErrorResponse(
          `You can only save up to ${MAX_TEMPLATES_PER_USER} cover letter templates. Please delete an existing template first.`
        );
      }

      const { data: existing, error: dupCheckError } = await supabase
        .from('cover_letter_templates')
        .select('id')
        .eq('user_id', userId)
        .eq('name', templateData.name.trim())
        .eq('is_active', true)
        .maybeSingle();

      if (dupCheckError) throw dupCheckError;

      if (existing) {
        return this._createErrorResponse('A template with this name already exists');
      }

      const { data, error } = await supabase
        .from('cover_letter_templates')
        .insert({
          user_id: userId,
          name: templateData.name.trim(),
          content: templateData.content.trim()
        })
        .select('id, name, content, usage_count, created_at')
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[CoverLetterTemplateService] createTemplate:', error);
      return { success: false, error: 'Failed to create template. Please try again.' };
    }
  }

  async updateTemplate(templateId, userId, updates) {
    if (!templateId || !userId) {
      return { success: false, error: 'Template ID and User ID are required' };
    }

    try {
      const validation = this._validateTemplateData(updates, true);
      if (!validation.isValid) {
        return { success: false, error: validation.error };
      }

      const { data: existing, error: checkError } = await supabase
        .from('cover_letter_templates')
        .select('id, name')
        .eq('id', templateId)
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();

      if (checkError) throw checkError;
      if (!existing) {
        return { success: false, error: 'Template not found' };
      }

      if (updates.name && updates.name.trim() !== existing.name) {
        const { data: duplicate, error: duplicateError } = await supabase
          .from('cover_letter_templates')
          .select('id')
          .eq('user_id', userId)
          .eq('name', updates.name.trim())
          .eq('is_active', true)
          .neq('id', templateId)
          .maybeSingle();

        if (duplicateError) throw duplicateError;

        if (duplicate) {
          return { success: false, error: 'A template with this name already exists' };
        }
      }

      const updateData = {};
      if (updates.name) updateData.name = updates.name.trim();
      if (updates.content) updateData.content = updates.content.trim();

      const { data, error } = await supabase
        .from('cover_letter_templates')
        .update(updateData)
        .eq('id', templateId)
        .eq('user_id', userId)
        .select('id, name, content, usage_count, created_at')
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[CoverLetterTemplateService] updateTemplate:', error);
      return { success: false, error: 'Failed to update template. Please try again.' };
    }
  }

  async deleteTemplate(templateId, userId) {
    if (!templateId || !userId) {
      return { success: false, error: 'Template ID and User ID are required' };
    }

    try {
      const { error } = await supabase
        .from('cover_letter_templates')
        .update({ is_active: false })
        .eq('id', templateId)
        .eq('user_id', userId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('[CoverLetterTemplateService] deleteTemplate:', error);
      return { success: false, error: 'Failed to delete template. Please try again.' };
    }
  }

  async useTemplate(templateId, userId) {
    if (!templateId || !userId) return;

    try {
      const { error } = await supabase.rpc('increment_cover_letter_template_usage', {
        template_id: templateId,
        user_id_param: userId
      });
      if (error) throw error;
    } catch (error) {
      console.error('[CoverLetterTemplateService] useTemplate:', error);
    }  }

  _validateTemplateData(data, isUpdate = false) {
    if (!data) return { isValid: false, error: 'Template data is required' };

    if (!isUpdate || data.name !== undefined) {
      if (!data.name || !data.name.trim()) {
        return { isValid: false, error: 'Template name is required' };
      }
      if (data.name.trim().length > MAX_NAME_LENGTH) {
        return { isValid: false, error: `Template name must be ${MAX_NAME_LENGTH} characters or less` };
      }
    }

    if (!isUpdate || data.content !== undefined) {
      if (!data.content || !data.content.trim()) {
        return { isValid: false, error: 'Template content is required' };
      }
      if (data.content.trim().length > MAX_CONTENT_LENGTH) {
        return { isValid: false, error: `Template content must be ${MAX_CONTENT_LENGTH} characters or less` };
      }
    }

    return { isValid: true };
  }

  _createSuccessResponse(data, count) {
    return { success: true, data, count };
  }

  _createErrorResponse(error, defaultData = null, defaultCount = 0) {
    return { success: false, error, data: defaultData, count: defaultCount };
  }
}

const coverLetterTemplateService = new CoverLetterTemplateService();
export default coverLetterTemplateService;
